import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { Loaded } from '../catalog/pipeline/useInsights';

export type McpTool = { name: string; description?: string };

/**
 * How long the endpoint has to answer — the deadline the endpoint's own tools
 * give the sources they ask. Past it the page says the endpoint did not answer,
 * rather than asking for ever.
 */
export const ANSWER_TIMEOUT_MS = 15_000;

/** The id of the page's one request, which picks its answer out of a stream. */
const REQUEST_ID = 1;

/**
 * The tools the portal's own MCP endpoint offers, asked of the endpoint itself
 * (DX-501, specs/mcp-and-cli.md AC-1): a page that wrote the list down would
 * go on claiming a tool after it was removed, and miss one that was added.
 *
 * The question is MCP's own `tools/list`, sent as the signed-in colleague —
 * the endpoint accepts a Backstage token as well as its own. The endpoint
 * answers in the streamable-HTTP transport's event-stream framing; a plain
 * JSON answer is read as well.
 *
 * `address` is where the endpoint is, as this portal is deployed: the page
 * names it and the Claude Code command uses it. It is known before the
 * question is asked, so it is handed on before the answer arrives.
 *
 * Each visit asks once, and so appears in the endpoint's log and metrics as a
 * `tools/list` request (docs/runbooks/mcp-server.md).
 */
export function useMcpTools(): Loaded<McpTool[]> & { address?: string } {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<Loaded<McpTool[]> & { address?: string }>({
    loading: true,
  });

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const deadline = window.setTimeout(
      () => controller.abort(),
      ANSWER_TIMEOUT_MS,
    );
    (async () => {
      let address: string | undefined;
      try {
        address = await discoveryApi.getBaseUrl('mcp');
        if (active) setState({ loading: true, address });
        const res = await fetchApi.fetch(address, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json, text/event-stream',
          },
          body: JSON.stringify({
            jsonrpc: '2.0',
            id: REQUEST_ID,
            method: 'tools/list',
            params: {},
          }),
          signal: controller.signal,
        });
        if (!res.ok) {
          throw new Error(`${res.status} ${res.statusText}`.trim());
        }
        const reply = readRpc(await res.text(), REQUEST_ID);
        const tools = reply?.result?.tools;
        if (!Array.isArray(tools)) {
          throw new Error('the endpoint did not answer with a tool list');
        }
        const listed = tools
          .filter((t): t is McpTool => typeof t?.name === 'string')
          .map(({ name, description }) => ({ name, description }));
        if (active) setState({ loading: false, data: listed, address });
      } catch (err) {
        if (active) setState({ loading: false, error: err as Error, address });
      } finally {
        window.clearTimeout(deadline);
      }
    })();
    return () => {
      active = false;
      window.clearTimeout(deadline);
      controller.abort();
    };
  }, [discoveryApi, fetchApi]);

  return state;
}

type RpcReply = {
  id?: unknown;
  result?: { tools?: { name?: unknown; description?: string }[] };
  error?: { message?: string };
};

/**
 * The JSON-RPC reply to request `id`, from either framing. An event stream may
 * carry other messages before the reply — a notification, a priming event — so
 * each event is read on its own: its `data:` lines joined, as the format says,
 * and the one answering `id` taken. A plain JSON body is the reply itself.
 */
export function readRpc(text: string, id: unknown = REQUEST_ID): RpcReply {
  const events = text
    .split(/\r?\n\r?\n/)
    .map(event =>
      event
        .split(/\r?\n/)
        .filter(line => line.startsWith('data:'))
        .map(line => line.slice('data:'.length).replace(/^ /, ''))
        .join('\n'),
    )
    .filter(Boolean);
  if (!events.length) return JSON.parse(text);
  const replies = events.flatMap(data => {
    try {
      return [JSON.parse(data) as RpcReply];
    } catch {
      return [];
    }
  });
  const reply = replies.find(r => r.id === id);
  if (!reply) throw new Error('the endpoint sent no reply to the question');
  return reply;
}
